import { FC, useCallback, useMemo } from "react";
import Typography from "@mui/material/Typography";
import { IconButton, Skeleton, Stack, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ResourceExplorerForecastedCostsPopover } from "./ResourceExplorerForecastedCostsPopover";
import { forecastedCostOptionLabels } from "../utils/constants";
import { money } from "@/utils/numeral/money";
import { TrendChip } from "@/components/common/chips/TrendChip";
import { ForecastedCostRe, ForecastOption } from "@/services/cloudchipr.api";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { getTrendTooltipTitle } from "@/components/pages/dashboard/utils/helpers/trend-tooltip-title/getTrendTooltipTitle";
import { ForecastedType } from "@/components/pages/dashboard/utils/types/types";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { metaDataTitleHeight } from "@/components/pages/common/info-cards/InfoCard";
import { useHover } from "@/utils/hooks/useHover.hook";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { billingExplorerDisplayForecastSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/chart/settings/billingExplorerDisplayForecastSelector";
import { resourceExplorerDataPointSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerDataPointSelector";
import { setResourceExplorerDataPoint } from "@/store/resource-explorer/resourceExplorerSlice";
import { getResourceExplorerDataThunk } from "@/store/resource-explorer/thunks/widgets/getResourceExplorerDataThunk";
import { resourceExplorerPathLastItemFilterTreeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/path/resourceExplorerPathLastItemFilterTreeSelector";
import { updateBillingExplorerChartSettingsThunk } from "@/store/resource-explorer/thunks/resource-explorer/updateBillingExplorerChartSettingsThunk";

interface ResourceExplorerForecastedCostsProps {
  forecastedCosts?: ForecastedCostRe;
  selectedForecastOption: ForecastOption;
  onForecastChange?(forecastOption: ForecastOption): void;
  showTrend?: boolean;
  loading?: boolean;
  canBeHidden?: boolean;
}

export const ResourceExplorerForecastedCosts: FC<
  ResourceExplorerForecastedCostsProps
> = ({
  forecastedCosts,
  selectedForecastOption,
  onForecastChange,
  showTrend = true,
  canBeHidden = true,
  loading,
}) => {
  const { ref, hovered } = useHover();
  const { anchor, open, openMenu, closeMenu } = useMenuHook();

  const dispatch = useAppDispatch();
  const dataPoint = useAppSelector(resourceExplorerDataPointSelector);
  const displayForecast = useAppSelector(
    billingExplorerDisplayForecastSelector,
  );
  const pathLastItemFilterTree = useAppSelector(
    resourceExplorerPathLastItemFilterTreeSelector,
  );

  const selectedForecast = useMemo(() => {
    return forecastedCosts?.find(
      (item) =>
        item.forecast_option.toLowerCase() ===
        selectedForecastOption.toLowerCase(),
    );
  }, [forecastedCosts, selectedForecastOption]);

  const handleDisplayForecastChange = useCallback(
    (checked: boolean) => {
      dispatch(
        updateBillingExplorerChartSettingsThunk({ display_forecast: checked }),
      );
      if (dataPoint !== "daily") {
        dispatch(setResourceExplorerDataPoint("daily"));
        dispatch(getResourceExplorerDataThunk(pathLastItemFilterTree));
      }
    },
    [dispatch, dataPoint, pathLastItemFilterTree],
  );

  const forecastedType: ForecastedType =
    selectedForecastOption === "month"
      ? "forecasted_month_end_cost"
      : "forecasted_quarter_end_cost";

  const trendTooltipTitle = getTrendTooltipTitle(
    selectedForecast?.date_from,
    selectedForecast?.date_to,
    selectedForecast?.previous_period_from,
    selectedForecast?.previous_period_to,
    forecastedType,
  );

  const fromDate = formatDate(selectedForecast?.date_from ?? "", {
    type: "date",
  });
  const toDate = formatDate(selectedForecast?.date_to ?? "", { type: "date" });
  const withPopover = !!onForecastChange;

  return (
    <Stack justifyContent="flex-start" height="100%" p={1} ref={ref}>
      <Stack
        direction="row"
        spacing={withPopover ? 0.5 : 1}
        alignItems="center"
        height={metaDataTitleHeight}
      >
        <Typography
          variant="subtitle2"
          color="text.secondary"
          fontWeight="regular"
          whiteSpace="nowrap"
        >
          Forecasted {forecastedCostOptionLabels.get(selectedForecastOption)}{" "}
          Cost
        </Typography>

        <Tooltip
          arrow
          placement="top"
          title={`Forecasted Cost from ${fromDate} to ${toDate}`}
        >
          <InfoOutlinedIcon color="action" sx={{ fontSize: 16 }} />
        </Tooltip>

        {canBeHidden && (
          <Tooltip
            title={!displayForecast ? "Display Forecast" : "Hide Forecast"}
            arrow
            placement="top"
          >
            <span>
              <IconButton
                onClick={() => handleDisplayForecastChange(!displayForecast)}
                size="small"
                sx={{ visibility: hovered ? "visible" : "hidden" }}
              >
                {!displayForecast ? (
                  <VisibilityOffOutlinedIcon fontSize="inherit" />
                ) : (
                  <VisibilityOutlinedIcon fontSize="inherit" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {withPopover && (
          <IconButton onClick={openMenu} size="small">
            {open ? (
              <ArrowDropUpIcon fontSize="medium" />
            ) : (
              <ArrowDropDownIcon fontSize="medium" />
            )}
          </IconButton>
        )}
      </Stack>

      <Stack
        spacing={1}
        direction="row"
        justifyContent="end"
        alignItems="center"
      >
        {loading ? (
          <Skeleton variant="text" width={100} />
        ) : (
          <Typography variant="h6" fontWeight="medium" mr={1}>
            ~{money(selectedForecast?.cost)}
          </Typography>
        )}

        {showTrend && (
          <TrendChip
            value={selectedForecast?.trend}
            tooltipTitle={trendTooltipTitle}
          />
        )}
      </Stack>

      {withPopover && (
        <ResourceExplorerForecastedCostsPopover
          open={open}
          anchor={anchor}
          closeMenu={closeMenu}
          options={forecastedCosts}
          onForecastChange={onForecastChange}
          selectedForecastOption={selectedForecastOption}
        />
      )}
    </Stack>
  );
};
